import { $, gsap } from "../shared/globals.js";

export function createMosaicEyesFeature({ prefersReducedMotion, mm }) {
    const EYES_CONFIG = {
        maxOffset: 50,
        smoothing: 0.12,
        deadZone: 0.01,
        blinkMinInterval: 2000,
        blinkMaxInterval: 6000,
        blinkDuration: 0.12,
        blinkScaleY: 0.1,
        doubleBlinkChance: 0.3,
    };

    let eyesAnimationEnabled = false;
    let eyesRafId = null;
    let eyesMouseX = 0;
    let eyesMouseY = 0;
    let eyesCurrentX = 0;
    let eyesCurrentY = 0;
    let blinkTimeoutId = null;
    let eyesFallbackId = null;
    let allEyePaths = [];
    let eyeInstances = [];

    function handleMouseMove(event) {
        eyesMouseX = (event.clientX / window.innerWidth) * 2 - 1;
        eyesMouseY = (event.clientY / window.innerHeight) * 2 - 1;
    }

    function scheduleNextBlink() {
        if (!eyesAnimationEnabled) return;

        const delay =
            EYES_CONFIG.blinkMinInterval +
            Math.random() * (EYES_CONFIG.blinkMaxInterval - EYES_CONFIG.blinkMinInterval);

        blinkTimeoutId = window.setTimeout(() => {
            performBlink();
        }, delay);
    }

    function performSingleBlink(onComplete) {
        if (!eyesAnimationEnabled || allEyePaths.length === 0) {
            if (onComplete) onComplete();
            return;
        }

        const blinkTl = gsap.timeline({ onComplete });

        blinkTl.to(allEyePaths, {
            scaleY: EYES_CONFIG.blinkScaleY,
            duration: EYES_CONFIG.blinkDuration,
            ease: "power2.in",
        });

        blinkTl.to(allEyePaths, {
            scaleY: 1,
            duration: EYES_CONFIG.blinkDuration,
            ease: "power2.out",
        });
    }

    function performBlink() {
        if (!eyesAnimationEnabled || allEyePaths.length === 0) return;

        const blinkTl = gsap.timeline({
            onComplete: () => {
                if (Math.random() < EYES_CONFIG.doubleBlinkChance) {
                    window.setTimeout(() => {
                        performSingleBlink(() => scheduleNextBlink());
                    }, 150);
                } else {
                    scheduleNextBlink();
                }
            },
        });

        blinkTl.to(allEyePaths, {
            scaleY: EYES_CONFIG.blinkScaleY,
            duration: EYES_CONFIG.blinkDuration,
            ease: "power2.in",
        });

        blinkTl.to(allEyePaths, {
            scaleY: 1,
            duration: EYES_CONFIG.blinkDuration,
            ease: "power2.out",
        });
    }

    function collectEyeInstances() {
        return Array.from(document.querySelectorAll("[data-mozaika-eyes]"))
            .map((svgMozaika) => {
                const eyeGroups = ["#oczka1", "#oczka_2", "#oczka_3"]
                    .map((selector) => svgMozaika.querySelector(selector))
                    .filter(Boolean);

                if (eyeGroups.length === 0) return null;

                const eyePaths = [];

                eyeGroups.forEach((group) => {
                    const paths = group.querySelectorAll("path");

                    paths.forEach((path) => {
                        eyePaths.push(path);
                    });
                });

                return {
                    eyeGroups,
                    eyePaths,
                };
            })
            .filter(Boolean);
    }

    function animateEyes() {
        if (!eyesAnimationEnabled) return;

        eyesCurrentX += (eyesMouseX - eyesCurrentX) * EYES_CONFIG.smoothing;
        eyesCurrentY += (eyesMouseY - eyesCurrentY) * EYES_CONFIG.smoothing;

        let offsetX = eyesCurrentX;
        let offsetY = eyesCurrentY;

        if (Math.abs(offsetX) < EYES_CONFIG.deadZone) offsetX = 0;
        if (Math.abs(offsetY) < EYES_CONFIG.deadZone) offsetY = 0;

        const translateX = offsetX * EYES_CONFIG.maxOffset;
        const translateY = offsetY * EYES_CONFIG.maxOffset;

        eyeInstances.forEach((instance) => {
            instance.eyeGroups.forEach((group) => {
                gsap.set(group, {
                    x: translateX,
                    y: translateY,
                });
            });
        });

        eyesRafId = requestAnimationFrame(animateEyes);
    }

    function initEyesAnimation() {
        if (eyesAnimationEnabled || prefersReducedMotion) return;

        eyeInstances = collectEyeInstances();

        if (eyeInstances.length === 0) return;

        eyesAnimationEnabled = true;
        allEyePaths = [];

        eyeInstances.forEach((instance) => {
            instance.eyePaths.forEach((path) => {
                allEyePaths.push(path);
                const bbox = path.getBBox();
                const centerX = bbox.x + bbox.width / 2;
                const centerY = bbox.y + bbox.height / 2;

                gsap.set(path, { svgOrigin: `${centerX} ${centerY}` });
            });
        });

        $(document).on("mousemove.eyes", handleMouseMove);
        animateEyes();
        scheduleNextBlink();
    }

    function destroy() {
        if (eyesFallbackId) {
            window.clearTimeout(eyesFallbackId);
            eyesFallbackId = null;
        }

        if (eyesRafId) {
            cancelAnimationFrame(eyesRafId);
            eyesRafId = null;
        }

        if (blinkTimeoutId) {
            window.clearTimeout(blinkTimeoutId);
            blinkTimeoutId = null;
        }

        $(document).off("mousemove.eyes", handleMouseMove);
        eyesAnimationEnabled = false;
        eyesCurrentX = 0;
        eyesCurrentY = 0;
        allEyePaths = [];
        eyeInstances.forEach((instance) => {
            instance.eyeGroups.forEach((group) => {
                gsap.set(group, { x: 0, y: 0 });
            });
        });
        eyeInstances = [];
    }

    function register() {
        mm.add("(min-width: 768px)", () => {
            $(window).on("load.eyes", initEyesAnimation);

            eyesFallbackId = window.setTimeout(() => {
                initEyesAnimation();
            }, 500);

            return () => {
                destroy();
                $(window).off("load.eyes", initEyesAnimation);
            };
        });
    }

    return {
        register,
        destroy,
    };
}
